import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/modules/auth/stores/auth-store'

export interface GroupOption {
  id: number
  name: string
  subdomain: string
}

export interface InvestorSetOption {
  id: number
  name: string
  entity_type: string
}

export const useInsightsStore = defineStore('Insights', () => {
  const authStore = useAuthStore()
  const route = useRoute()

  const baseUrl = import.meta.env.VITE_VENTURE360_API_URL.replace('/v3', '/v4')

  const result = ref<any>(null)
  const polling = ref(false)
  const pollingError = ref<string | null>(null)
  const groups = ref<GroupOption[]>([])
  const investorSets = ref<InvestorSetOption[]>([])

  function buildUrl(path: string, params: Record<string, any>) {
    const slug = route.params.slug
    const queryString = Object.entries(params)
      .filter(([_, v]) => v != null && v !== '' && !(Array.isArray(v) && v.length === 0))
      .flatMap(([k, v]) =>
        Array.isArray(v)
          ? v.map((vv) => `${encodeURIComponent(`${k}[]`)}=${encodeURIComponent(vv)}`)
          : [`${encodeURIComponent(k)}=${encodeURIComponent(v)}`],
      )
      .join('&')

    const url = `${baseUrl}/${slug}/insights${path}`
    return queryString ? `${url}?${queryString}` : url
  }

  async function apiGet(path: string, params: Record<string, any> = {}) {
    const url = buildUrl(path, params)
    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${authStore.access_token}`,
        'Content-Type': 'application/json',
      },
    })
    const data = await res.json()
    return { ok: res.ok, status: res.status, data }
  }

  const fetchGroups = async () => {
    if (groups.value.length > 0) return

    const slug = route.params.slug
    const url = `${baseUrl}/${slug}/insights/groups`
    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${authStore.access_token}`,
        'Content-Type': 'application/json',
      },
    })
    if (res.ok) {
      groups.value = await res.json()
    }
  }

  const fetchInvestorSets = async (groupId: number) => {
    investorSets.value = []
    const slug = route.params.slug
    const url = `${baseUrl}/${slug}/insights/investor-sets?group_id=${groupId}`
    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${authStore.access_token}`,
        'Content-Type': 'application/json',
      },
    })
    if (res.ok) {
      investorSets.value = await res.json()
    }
  }

  const pollForResults = async (jobId: string) => {
    const MAX_ATTEMPTS = 60
    const POLL_INTERVAL = 2000

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL))

      const { data } = await apiGet('/status', { job_id: jobId })

      if (data.status === 'success') {
        result.value = data.data
        polling.value = false
        return
      }

      if (data.status === 'error') {
        polling.value = false
        pollingError.value = data.error || 'Computation failed.'
        return
      }
    }

    polling.value = false
    pollingError.value = 'Computation timed out. Please try again.'
  }

  const fetchInsight = async (params: Record<string, any>) => {
    polling.value = true
    pollingError.value = null
    result.value = null

    try {
      const { data } = await apiGet('', params)

      if (data.status === 'success') {
        result.value = data.data
        polling.value = false
        return
      }

      if (data.status === 'pending' && data.job_id) {
        await pollForResults(data.job_id)
        return
      }

      polling.value = false
      pollingError.value = data.error || 'Unexpected response from server.'
    } catch (e: any) {
      polling.value = false
      pollingError.value = e.message || 'Failed to fetch insight.'
    }
  }

  const reset = () => {
    result.value = null
    polling.value = false
    pollingError.value = null
  }

  return {
    result,
    polling,
    pollingError,
    groups,
    investorSets,
    fetchGroups,
    fetchInvestorSets,
    fetchInsight,
    reset,
  }
})
